import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { AccessoriesService } from 'src/app/services/accessories/accessories.service';
import { AccessoriesModalService } from 'src/app/services/accessories-modal/accessories-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-accessories',
  templateUrl: './add-edit-accessories.component.html',
  styleUrls: ['./add-edit-accessories.component.scss']
})
export class AddEditAccessoriesComponent extends DestructorComponent implements OnInit {

  @Output() accessoryAdded = new EventEmitter<void>();
  @Output() accessoryUpdated = new EventEmitter<void>();

  accessoryForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedAccessory: any = null;

  constructor(
    private fb: FormBuilder,
    private _accessoriesService: AccessoriesService,
    private _accessoriesModalService: AccessoriesModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.accessoryForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      group: ['', [Validators.required, Validators.minLength(2)]],
      type: ['', [Validators.required]]
    });
  }

  // Getter methods for form controls
  get name() { return this.accessoryForm.get('name'); }
  get group() { return this.accessoryForm.get('group'); }
  get type() { return this.accessoryForm.get('type'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_accessory_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceAccessory = this._accessoriesModalService.getSelectedAccessory();
        if (serviceAccessory) {
          this.isEditMode = true;
          this.selectedAccessory = serviceAccessory;
          this.populateForm(this.selectedAccessory);
        } else {
          this.isEditMode = false;
          this.selectedAccessory = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._accessoriesModalService.clearSelectedAccessory();
        this.resetForm();
      });
    }
  }

  private populateForm(accessory: any): void {
    this.accessoryForm.patchValue({
      name: accessory.name || '',
      group: accessory.group || '',
      type: accessory.type || ''
    });
  }

  private resetForm(): void {
    this.accessoryForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.accessoryForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_accessory_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.accessoryForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.accessoryForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._accessoriesService.UpdateData(this.selectedAccessory._id, submitData)
        : this._accessoriesService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Accessory operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Accessory has been successfully updated!"
                : "Accessory has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.accessoryUpdated.emit();
                } else {
                  this.accessoryAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Accessory operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.accessoryForm);
    }
  }
}
